import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import TopBar from "../../components/TopBar";
import ArchiveRetentionTab from "./ArchiveRetentionTab";
import AnalyticsWorkspace from "./AnalyticsWorkspace";
import type { CameraAnalyticsConfig } from "./AnalyticsTab";
import OnvifConnectionTab from "./OnvifConnectionTab";
import VideoQualityCapabilitiesTab from "./VideoQualityCapabilitiesTab";
import CameraLocationTab from "./CameraLocationTab";
import BillingPlansTab from "./BillingPlansTab";
import GatewayCameraOnboardingForm from "./GatewayCameraOnboardingForm";
import {
  isDirectOnvifOnboardingFailure,
  onboardDirectOnvifCamera,
} from "./directOnvifOnboarding";
import { cognitoUserPoolsTokenProvider } from "aws-amplify/auth/cognito";
import { AccessRole, listAccessRoles } from "../../services/accessRolesService";
import { roleFromTokenClaims } from "../../auth/portalAccess";
import "./Cameras.css";
import {
  pageHeaderStyle,
  cameraListContainerStyle,
  cameraListHeaderStyle,
  errorMessageStyle,
  inputStyle,
  fieldLabelStyle,
  fieldHintStyle,
  btnPrimary,
  btnOutline,
  disabledButtonStyle,
  modalOverlay,
  modalCard,
  modalHeaderStyle,
  modalActionsStyle,
  statusContainerStyle,
  twoColumnGridStyle,
  profileRowStyle,
  btnX,
  summaryGridStyle,
  summaryCardStyle,
  summaryLabelStyle,
  summaryValueStyle,
  summaryDescriptionStyle,
  summaryIconStyle,
  fleetPanelStyle,
  fleetHeaderStyle,
  fleetTitleStyle,
  fleetSubtitleStyle,
  fleetResultCountStyle,
  fleetToolbarStyle,
  fleetSearchWrapperStyle,
  fleetSearchIconStyle,
  fleetSearchInputStyle,
  fleetFiltersStyle,
  fleetFilterButtonStyle,
  fleetFilterButtonActiveStyle,
  fleetListStyle,
  fleetCameraRowStyle,
  fleetStatusRailStyle,
  fleetCameraIdentityStyle,
  fleetThumbnailStyle,
  fleetThumbnailPlaceholderStyle,
  fleetCameraIconStyle,
  fleetCameraNameWrapperStyle,
  fleetCameraNameStyle,
  fleetCameraIdStyle,
  fleetConnectionStyle,
  fleetStatusPillStyle,
  fleetOnlinePillStyle,
  fleetOfflinePillStyle,
  fleetDisabledPillStyle,
  fleetUnknownPillStyle,
  fleetStatusDotStyle,
  fleetConnectionTextStyle,
  fleetDetailStyle,
  fleetDetailLabelStyle,
  fleetDetailValueStyle,
  fleetPlanBadgeStyle,
  fleetActionButtonStyle,
  fleetEmptyStyle,
  fleetEmptyIconStyle,
  fleetEmptyTitleStyle,
  fleetEmptyDescriptionStyle,
  cameraDetailsOverlayStyle,
  cameraDetailsModalStyle,
  cameraDetailsHeaderStyle,
  cameraDetailsIdentityStyle,
  cameraDetailsThumbnailStyle,
  cameraDetailsThumbnailPlaceholderStyle,
  cameraDetailsTitleStyle,
  cameraTagRowStyle,
  cameraStatusTagStyle,
  cameraDetailsCloseStyle,
  cameraDetailsTabsStyle,
  cameraTabStyle,
  cameraTabActiveStyle,
  cameraDetailsBodyStyle,
  cameraInfoLayoutStyle,
  generalInfoLaunchCardStyle,
  generalInfoLaunchIconStyle,
  generalInfoLaunchTitleStyle,
  generalInfoLaunchDescriptionStyle,
  generalInfoLaunchArrowStyle,
  generalInfoExpandedHeaderStyle,
  cameraInfoSectionTitleTextStyle,
  generalInfoExpandedSubtitleStyle,
  generalInfoBackButtonStyle,
  generalInfoContentGridStyle,
  generalInfoMainColumnStyle,
  generalInfoVerticalListStyle,
  generalInfoVerticalItemStyle,
  generalInfoVerticalLabelStyle,
  generalInfoVerticalValueStyle,
  generalInfoVerticalStatusListStyle,
  generalInfoDetailsColumnStyle,
  generalInfoThumbnailColumnStyle,
  generalInfoSectionLabelStyle,
  generalInfoListStyle,
  generalInfoListRowStyle,
  generalInfoListLabelStyle,
  generalInfoListValueStyle,
  generalInfoThumbnailCardStyle,
  generalInfoThumbnailStyle,
  generalInfoThumbnailEmptyStyle,
  generalInfoThumbnailFooterStyle,
  generalInfoThumbnailNameStyle,
  generalInfoThumbnailTimeStyle,
  generalInfoLiveBadgeStyle,
  generalInfoOfflineBadgeStyle,
  generalInfoStatusCardStyle,
  cameraInfoCardStyle,
  cameraInfoSectionTitleStyle,
  cameraInfoRowsStyle,
  cameraInfoRowStyle,
  cameraInfoLabelStyle,
  cameraInfoValueStyle,
  cameraInfoDividerStyle,
  availabilityLineStyle,
  analyticsHeadingStyle,
  analyticsLineStyle,
  generalOverviewStyle,
  qualitySummaryStyle,
  sectionHeadingRowStyle,
  sectionEyebrowStyle,
  sectionTitleStyle,
  sectionStatusBadgeStyle,
  overviewMetricGridStyle,
  qualityMetricGridStyle,
  overviewMetricStyle,
  overviewMetricLabelStyle,
  overviewMetricValueStyle,
  generalTabPanelStyle,
  generalFormGridStyle,
  generalDescriptionFieldStyle,
  generalFieldStyle,
  cameraGeneralInputStyle,
  cameraDescriptionStyle,
  toggleListStyle,
  toggleSettingRowStyle,
  toggleSettingLabelStyle,
  toggleSettingHelpStyle,
  toggleTrackStyle,
  toggleThumbStyle,
  qualityTabPanelStyle,
  qualityConnectionBannerStyle,
  qualityReadButtonStyle,
  qualityFormStyle,
  qualityRowStyle,
  qualityRowLabelStyle,
  qualityRowContentStyle,
  qualitySelectStyle,
  qualityRangeLineStyle,
  qualityRangeStyle,
  qualityBitrateRangeStyle,
  qualityRangeLimitStyle,
  qualityBlueLabelStyle,
  qualityNumberStyle,
  qualityUnitStyle,
  qualityCheckLabelStyle,
  qualityEmptyStyle,
  supportTabPanelStyle,
  supportConnectionCardStyle,
  supportCardEyebrowStyle,
  supportCardTitleStyle,
  supportCardDescriptionStyle,
  supportStatusPillsStyle,
  supportSuccessPillStyle,
  supportSuccessDotStyle,
  supportDetailsCardStyle,
  supportDetailsHeaderStyle,
  supportDetailsTitleStyle,
  supportDetailsSubtitleStyle,
  supportDemoBadgeStyle,
  supportDetailsGridStyle,
  supportFieldStyle,
  supportFieldLabelStyle,
  supportReadOnlyInputStyle,
  supportEditableInputStyle,
  supportMessageStyle,
  cameraDetailsFooterStyle,
  cameraSaveButtonStyle,
  cameraSecondaryActionButtonStyle,
  cameraCloseButtonStyle,
  cameraFooterStatusStyle
} from "./Cameras.styles";
import {
  classifyGatewayFinalizationResponse,
  findGatewayCamera,
  gatewayFinalizationFailure,
} from "./gatewayFinalization";
type Props = {
  user?: any;
  onSignOut?: () => void;
};
type ProfileOption = {
  token: string;
  name: string;
};

type CameraDeviceDetails = {
  manufacturer: string;
  model: string;
  serialNumber: string;
  description: string;
  motionEvents: boolean;
  cameraAnalytics: boolean;
  mic: boolean;
  alerts: boolean;
  cloudRecording: boolean;
  onlineFrom: string;
  httpAvailable: boolean;
  p2pAvailable: boolean;
  onvifConnectionAvailable: boolean;
  onvifAuthenticationSuccessful: boolean;
  firmwareVersion: string;
  hardwareId: string;
  macAddress: string;
  p2pUsername: string;
  p2pPassword: string;
  publicIp: string;
  analyticsModules: {
    cellMotionDetector: boolean;
    lineCrossingDetector: boolean;
    intrusionDetector: boolean;
    tamperingDetector: boolean;
  };
};

type OnvifQualityProfile = {
  token: string;
  name: string;
  resolutions: string[];
  resolution: string;
  fpsMin: number;
  fpsMax: number;
  fps: number;
  bitrateMin: number;
  bitrateMax: number;
  bitrate: number;
  imageQuality: number;
  vbr: boolean;
  setAutomatically: boolean;
};

type CameraDetailsTab =
  | "info"
  | "general"
  | "archive"
  | "analytics"
  | "onvif"
  | "quality"
  | "billing"
  | "location"
  | "support";

type RemoteSupportSession = {
  sessionId: string;
  targetType: "camera" | "gateway";
  label: string;
  url: string;
  expiresAt: string;
  expiresInSeconds: number;
};

type OnvifQualityLoadResult = {
  profiles: OnvifQualityProfile[];
  source: "onvif";
  message: string;
  deviceDetails?: Partial<CameraDeviceDetails>;
};

const ONVIF_PROFILES_API =
  "https://2owz15gkce.execute-api.us-east-1.amazonaws.com/default/qumra-onvif-get-profiles";
const CAMERAS_API =
  "https://f0usr04663.execute-api.us-east-1.amazonaws.com/cameras";
const SUPPORT_SESSIONS_API =
  "https://f0usr04663.execute-api.us-east-1.amazonaws.com/support-sessions";
type Camera = {
  PK: string;
  SK: string;
  cameraId: string;
  customerId: string;
  entityType: string;
  name: string;
  host: string;
  httpPort: number;
  rtspPort: number;
  archiveHours?: number;
  createdAt: string;
  updatedAt: string;
  thumbnailKey?: string;
  thumbnailUrl?: string;
  thumbnailUpdatedAt?: string;
  enabled?: boolean;
  motionEventsEnabled?: boolean;
  motionEventsStatus?: string;
  motionEventsError?: string;
  motionEventsLastSeenAt?: string;
  p2pStatus?: string;
  videoStatus?: string;
  registrationCode?: string;
  connectionType?: "direct" | "gateway";
  edgeGatewayId?: string;
  edgeCameraId?: string;
  edgeLocalHost?: string;
  edgeHttpPort?: number;
  edgeRtspPort?: number;
  onboardingStatus?: string;
  onboardingError?: string;
  assignedTo?: string;
  accessRoleId?: string;
  accessRoleName?: string;
  integratorUserId?: string;
  integratorName?: string;
  cameraAddedByUserId?: string;
  cameraAddedByName?: string;
  cameraAddedByRole?: string;
  planName?: string;
  planType?: string;
  onvif?: {
    path?: string;
    port?: number;
    profileToken?: string;
  };
  username?: string;
  password?: string;
  analyticsConfig?: CameraAnalyticsConfig;
  location?:
    | string
    | {
        latitude: number;
        longitude: number;
        label?: string;
      }
    | null;
  deletionStatus?: string;
  deletionError?: string;
  deviceDetails?: Partial<CameraDeviceDetails>;
  demoQualityProfiles?: OnvifQualityProfile[];
};
function normalizeHost(input: string) {
  return input
    .trim()
    .replace(/^https?:\/\//i, "")
    .replace(/\/+$/, "");
}

type CameraListResponse = {
  customerId: string;
  count: number;
  items: Camera[];
  nextToken?: string | null;
};

function base64UrlDecode(input: string) {
  const b64 = input.replace(/-/g, "+").replace(/_/g, "/");
  const pad = b64.length % 4 ? "=".repeat(4 - (b64.length % 4)) : "";
  return atob(b64 + pad);
}

async function getIdTokenOrThrow() {
  const tokens = await cognitoUserPoolsTokenProvider.getTokens();
  if (!tokens?.idToken) {
    throw new Error("No ID token found. Please sign out and sign in again.");
  }
  return tokens.idToken.toString();
}

async function readResponse(res: Response) {
  const text = await res.text();
  try {
    return { text, json: JSON.parse(text) };
  } catch {
    return { text, json: null };
  }
}

function getErrorMessage(error: unknown) {
  return error instanceof Error
    ? error.message
    : "An unexpected error occurred.";
}

function cloneQualityProfiles(profiles: OnvifQualityProfile[]) {
  return profiles.map((profile) => ({
    ...profile,
    resolutions: [...profile.resolutions],
  }));
}

function createCameraDeviceDetails(camera: Camera): CameraDeviceDetails {
  const online = getCameraConnectionState(camera) === "online";
  const deviceDetails = camera.deviceDetails || {};
  return {
    manufacturer: deviceDetails.manufacturer || "Not loaded from camera",
    model: deviceDetails.model || "Not loaded from camera",
    serialNumber: deviceDetails.serialNumber || "Not loaded from camera",
    description: "",
    motionEvents: isMotionEventsEnabled(camera),
    cameraAnalytics: false,
    mic: false,
    alerts: false,
    cloudRecording: Boolean(camera.planName && camera.planName !== "No plan"),
    onlineFrom: online
      ? formatDate(camera.updatedAt)
      : "Offline or unavailable",
    httpAvailable: Boolean(camera.host && camera.httpPort),
    p2pAvailable:
      camera.p2pStatus?.toLowerCase().includes("connected") ?? false,
    onvifConnectionAvailable: Boolean(
      camera.onvif?.profileToken || camera.onvif?.port,
    ),
    onvifAuthenticationSuccessful: Boolean(camera.username),
    firmwareVersion: deviceDetails.firmwareVersion || "Not loaded from camera",
    hardwareId: deviceDetails.hardwareId || "Not loaded from camera",
    macAddress: deviceDetails.macAddress || "Not loaded from camera",
    p2pUsername: camera.username ? "Configured securely" : "Not configured",
    p2pPassword: "••••••••",
    publicIp: camera.host,
    analyticsModules: {
      cellMotionDetector: false,
      lineCrossingDetector: false,
      intrusionDetector: false,
      tamperingDetector: false,
    },
  };
}

async function readOnvifQuality(
  camera: Camera,
): Promise<OnvifQualityLoadResult> {
  if (!camera.host) {
    throw new Error("This camera has no host in the camera API response.");
  }
  if (!camera.username || !camera.password) {
    throw new Error(
      "This camera has no ONVIF username or password in the camera API response.",
    );
  }

  const onvifPort = Number(camera.onvif?.port || camera.httpPort);
  if (!isValidPort(onvifPort)) {
    throw new Error("This camera has no valid ONVIF/HTTP port.");
  }

  const configuredPath = camera.onvif?.path?.trim();
  const onvifPath =
    configuredPath && configuredPath !== "(default)"
      ? configuredPath
      : undefined;

  const response = await fetch(ONVIF_PROFILES_API, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      httpHost: normalizeHost(camera.host),
      host: normalizeHost(camera.host),
      httpPort: onvifPort,
      username: camera.username,
      password: camera.password,
      ...(onvifPath ? { path: onvifPath } : {}),
      includeImaging: true,
    }),
  });

  const { text, json } = await readResponse(response);
  if (!response.ok) {
    const message =
      json?.message || text || response.statusText || "ONVIF request failed.";
    throw new Error(`${response.status}: ${message}`);
  }
  if (!json?.success) {
    throw new Error(json?.message || "The camera rejected the ONVIF request.");
  }

  const rawChannels = Array.isArray(json?.channels) ? json.channels : [];

  const profiles: OnvifQualityProfile[] = rawChannels.map(
    (channel: any, index: number) => {
      const video = channel?.video || {};
      const width = Number(video?.resolution?.width);
      const height = Number(video?.resolution?.height);
      const fps = Number(video?.fps);
      const bitrate = Number(video?.bitrateKbps);
      const quality = Number(video?.quality);
      const resolutionDisplay =
        video?.resolution?.display ||
        (width > 0 && height > 0 ? `${width}x${height}` : "Unavailable");

      return {
        token: String(
          channel?.profileToken ??
            channel?.token ??
            channel?.value ??
            camera.onvif?.profileToken ??
            index,
        ),
        name: String(
          channel?.label ?? channel?.name ?? `Profile ${index + 1}`,
        ),
        resolutions:
          resolutionDisplay !== "Unavailable" ? [resolutionDisplay] : [],
        resolution: resolutionDisplay,
        fpsMin: 1,
        fpsMax: Math.max(Number.isFinite(fps) && fps > 0 ? fps : 1, 30),
        fps: Number.isFinite(fps) && fps > 0 ? fps : 0,
        bitrateMin: 0,
        bitrateMax: Math.max(
          Number.isFinite(bitrate) && bitrate > 0 ? bitrate : 1,
          8192,
        ),
        bitrate: Number.isFinite(bitrate) && bitrate > 0 ? bitrate : 0,
        imageQuality: Number.isFinite(quality) ? quality : 0,
        vbr: false,
        setAutomatically: false,
      };
    },
  );

  if (profiles.length === 0) {
    throw new Error(
      "The camera connected through ONVIF, but no stream channels were returned.",
    );
  }

  const metadata = json?.camera || {};
  const matched = json?.matched || {};
  const deviceDetails: Partial<CameraDeviceDetails> = {
    manufacturer: metadata?.manufacturer || "Unavailable",
    model: metadata?.model || "Unavailable",
    serialNumber: metadata?.serialNumber || "Unavailable",
    firmwareVersion: metadata?.firmwareVersion || "Unavailable",
    hardwareId:
      metadata?.hardwareId !== undefined
        ? String(metadata.hardwareId)
        : "Unavailable",
    macAddress: Array.isArray(metadata?.macAddresses)
      ? metadata.macAddresses.join(", ")
      : "Unavailable",
    publicIp: matched?.host || camera.host,
    httpAvailable: true,
    onvifConnectionAvailable: true,
    onvifAuthenticationSuccessful: true,
  };

  return {
    profiles,
    source: "onvif",
    deviceDetails,
    message: `Connected through ONVIF. Loaded ${profiles.length} stream${
      profiles.length === 1 ? "" : "s"
    } with live resolution, FPS, and bitrate.`,
  };
}
async function saveOnvifQuality(
  _camera: Camera,
  _profile: OnvifQualityProfile,
) {
  throw new Error(
    "Quality settings were not saved because a production ONVIF quality update endpoint is not configured.",
  );
}
type CameraConnectionState = "online" | "offline" | "unknown";
function isCameraEnabled(camera: Camera) {
  return camera.enabled !== false;
}

function isMotionEventsEnabled(camera: Camera) {
  if (typeof camera.motionEventsEnabled === "boolean") {
    return camera.motionEventsEnabled;
  }

  const status = String(camera.motionEventsStatus || "")
    .trim()
    .toUpperCase();

  return ["ACTIVE", "CONNECTED", "CONNECTING", "ENABLED", "PENDING", "RECONNECTING"].includes(
    status,
  );
}

function motionEventsHelp(camera: Camera, enabled: boolean) {
  const status = String(camera.motionEventsStatus || "").trim();
  const error = String(camera.motionEventsError || "").trim();

  if (error) {
    return `Collector status: ${status || "ERROR"}. ${error}`;
  }

  if (status) {
    return `Collector status: ${status}. Motion transitions will appear on the archive timeline when supported by the camera.`;
  }

  return enabled
    ? "Motion collection will start after this setting is saved."
    : "Enable this to collect real ONVIF motion transitions for the archive timeline.";
}

function getCameraConnectionState(camera: Camera): CameraConnectionState {
  const p2pStatus = camera.p2pStatus?.trim().toLowerCase() ?? "";
  const videoStatus = camera.videoStatus?.trim().toLowerCase() ?? "";
  if (
    p2pStatus.includes("disconnected") ||
    p2pStatus.includes("offline") ||
    p2pStatus.includes("not connected") ||
    p2pStatus.includes("registered") ||
    videoStatus.includes("offline")
  ) {
    return "offline";
  }
  if (
    p2pStatus.includes("connected") ||
    p2pStatus.includes("online") ||
    videoStatus.includes("online")
  ) {
    return "online";
  }
  return "unknown";
}

function getCameraConnectionMethod(camera: Camera) {
  if (camera.connectionType === "gateway") {
    return "Gateway";
  }
  if (camera.onvif?.profileToken || camera.onvif?.port || camera.onvif?.path) {
    return "ONVIF";
  }
  if (camera.p2pStatus || camera.registrationCode) {
    return "P&P";
  }
  // The current direct Admin Portal camera onboarding flow is ONVIF-based.
  return "ONVIF";
}
export default function Cameras({ user, onSignOut }: Props) {
  const email =
    user?.signInDetails?.loginId ||
    user?.attributes?.email ||
    user?.username ||
    "";
  const [cameras, setCameras] = useState<Camera[]>([]);
  const [loadingCameras, setLoadingCameras] = useState(false);
  const [cameraListError, setCameraListError] = useState("");
  const [showAddCamera, setShowAddCamera] = useState(false);
  const [connectionMethod, setConnectionMethod] = useState<"direct" | "gateway">("direct");
  const [selectedCamera, setSelectedCamera] = useState<Camera | null>(null);
  const [host, setHost] = useState("");
  const [httpPort, setHttpPort] = useState<number>(80);
  const [rtspPort, setRtspPort] = useState<number>(554);
  const [archiveHours, setArchiveHours] = useState<number>(48);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [cameraName, setCameraName] = useState("");
  const [accessRoles, setAccessRoles] = useState<AccessRole[]>([]);
  const [portalRole, setPortalRole] = useState<"OWNER" | "INTEGRATOR" | "ADMIN" | "VIEWER" | null>(null);
  const [selectedAccessRoleId, setSelectedAccessRoleId] = useState("");
  const normalizedHost = useMemo(() => normalizeHost(host), [host]);
  const [profiles, setProfiles] = useState<ProfileOption[]>([]);
  const [selectedProfileToken, setSelectedProfileToken] = useState("");
  const [loadingProfiles, setLoadingProfiles] = useState(false);
  const [creating, setCreating] = useState(false);
  const [status, setStatus] = useState("");
  const [canDeleteCameras, setCanDeleteCameras] = useState(false);

  const loadCameraAccessRoles = useCallback(async () => {
    try {
      const result = await listAccessRoles();
      setPortalRole(result.callerRole);
      setAccessRoles(Array.isArray(result.items) ? result.items : []);
      setSelectedAccessRoleId((current) =>
        current && result.items.some((role) => role.accessRoleId === current)
          ? current
          : result.items[0]?.accessRoleId || "",
      );
    } catch {
      // Camera listing remains available if the Access Role API is temporarily
      // unavailable during rollout. Backend authorization is still enforced.
      setAccessRoles([]);
    }
  }, []);

  const loadCameras = useCallback(async () => {
    setLoadingCameras(true);
    setCameraListError("");
    try {
      const idToken = await getIdTokenOrThrow();
      const res = await fetch(CAMERAS_API, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${idToken}`,
        },
      });
      const { text, json } = await readResponse(res);
      if (!res.ok)
        throw new Error(
          `${res.status}: ${json?.message || text || res.statusText || "Unable to load cameras."}`,
        );
      const response = json as CameraListResponse | null;
      const visibleItems = (response?.items ?? []).filter((camera) =>
        !["QUEUED", "PURGING"].includes(
          String(camera.deletionStatus || "").trim().toUpperCase(),
        ),
      );
      setCameras(visibleItems);
    } catch (error) {
      setCameras([]);
      setCameraListError(getErrorMessage(error));
    } finally {
      setLoadingCameras(false);
    }
  }, []);

  useEffect(() => {
    void loadCameras();
    void loadCameraAccessRoles();
  }, [loadCameras, loadCameraAccessRoles]);

  useEffect(() => {
    void cognitoUserPoolsTokenProvider
      .getTokens()
      .then((tokens) => {
        const role = roleFromTokenClaims(tokens?.idToken?.payload as any);
        setCanDeleteCameras(role === "OWNER" || role === "INTEGRATOR");
      })
      .catch(() => setCanDeleteCameras(false));
  }, []);
  function openModal() {
    setStatus("");
    setConnectionMethod("direct");
    setShowAddCamera(true);
  }
  function closeModal() {
    if (!creating && !loadingProfiles) setShowAddCamera(false);
  }

  async function getChannels() {
    setStatus("");
    setLoadingProfiles(true);
    setProfiles([]);
    setSelectedProfileToken("");
    if (!normalizedHost || !username.trim() || !password) {
      setStatus("Host, ONVIF username, and password are required.");
      setLoadingProfiles(false);
      return;
    }
    try {
      const res = await fetch(ONVIF_PROFILES_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          httpHost: normalizedHost,
          host: normalizedHost,
          httpPort: Number(httpPort),
          username: username.trim(),
          password,
        }),
      });
      const { text, json } = await readResponse(res);
      if (!res.ok)
        throw new Error(
          `${res.status}: ${json?.message || text || res.statusText || "Unable to retrieve ONVIF profiles."}`,
        );
      const channels = Array.isArray(json?.channels) ? json.channels : [];
      const parsed = channels.map((channel: any, index: number) => ({
        token: String(channel.value ?? channel.token ?? index),
        name: String(channel.label ?? channel.name ?? `Profile ${index + 1}`),
      }));
      if (!parsed.length) throw new Error("No ONVIF profiles were returned.");
      setProfiles(parsed);
      setSelectedProfileToken(parsed[0].token);
      if (json?.matched?.port) setHttpPort(Number(json.matched.port));
      setStatus(
        `Found ${parsed.length} ONVIF profile${parsed.length === 1 ? "" : "s"}.`,
      );
    } catch (error) {
      setStatus(`Get Channels failed: ${getErrorMessage(error)}`);
    } finally {
      setLoadingProfiles(false);
    }
  }

  // QUMRA_DIRECT_ONVIF_H264_ONBOARDING_BEGIN
  async function createCamera() {
    setStatus("");
    if (
      !normalizedHost ||
      !isValidPort(httpPort) ||
      !isValidPort(rtspPort) ||
      archiveHours < 0 ||
      !username.trim() ||
      !password ||
      !cameraName.trim() ||
      !selectedProfileToken ||
      (portalRole === "ADMIN" && !selectedAccessRoleId)
    ) {
      setStatus(
        portalRole === "ADMIN" && !selectedAccessRoleId
          ? "Choose one of the Access Roles assigned by your Integrator."
          : "Complete all required fields, use valid ports, and select an ONVIF profile.",
      );
      return;
    }

    setCreating(true);
    setStatus(
      "Creating the camera disabled, validating its H.264 profile, and then enabling it...",
    );

    try {
      const idToken = await getIdTokenOrThrow();
      const result = await onboardDirectOnvifCamera({
        apiBase: CAMERAS_API,
        idToken,
        profileToken: selectedProfileToken,
        createPayload: {
          name: cameraName.trim(),
          host: normalizedHost,
          httpPort: Number(httpPort),
          rtspPort: Number(rtspPort),
          archiveHours: Number(archiveHours),
          motionEventsEnabled: false,
          onvif: { profileToken: selectedProfileToken },
          ...(selectedAccessRoleId
            ? { accessRoleId: selectedAccessRoleId }
            : {}),
          credentials: { username: username.trim(), password },
        },
      });

      setPassword("");
      const successMessage = result.placementPending
        ? "Its H.264 profile was saved and a Media Gateway is starting. The stream will connect automatically."
        : "Its H.264 profile was saved and streaming was enabled successfully.";
      setStatus(`✅ Camera ${result.cameraId} was created. ${successMessage}`);
      await loadCameras();
    } catch (error) {
      if (isDirectOnvifOnboardingFailure(error) && error.cameraCreated) {
        setPassword("");
        const cameraLabel = error.cameraId
          ? `Camera ${error.cameraId}`
          : "The new camera";
        const safeState = error.metadataPersisted
          ? "Its H.264 metadata was saved, but it remains disabled because enablement failed."
          : "It remains disabled and was not allowed to start on the Media Gateway.";
        setStatus(
          `⚠️ ${cameraLabel} was created, but setup did not finish. ${safeState}\n${error.message}` ,
        );
        await loadCameras();
      } else {
        setStatus(`Create Camera failed: ${getErrorMessage(error)}`);
      }
    } finally {
      setCreating(false);
    }
  }
  // QUMRA_DIRECT_ONVIF_H264_ONBOARDING_END

  function openCameraDetails(camera: Camera) {
    setSelectedCamera(camera);
  }
  function closeCameraDetails() {
    setSelectedCamera(null);
  }
  function updateCameraDetails(updatedCamera: Camera) {
    setCameras((current) =>
      current.map((camera) =>
        camera.cameraId === updatedCamera.cameraId ? updatedCamera : camera,
      ),
    );
    setSelectedCamera(updatedCamera);
  }

  async function deleteCamera(camera: Camera) {
    const idToken = await getIdTokenOrThrow();
    const res = await fetch(
      `${CAMERAS_API}/${encodeURIComponent(camera.cameraId)}`,
      {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${idToken}`,
        },
      },
    );
    const { text, json } = await readResponse(res);
    if (!res.ok) {
      throw new Error(
        `${res.status}: ${
          json?.message || text || res.statusText || "Unable to delete camera."
        }`,
      );
    }

    setCameras((current) =>
      current.filter((item) => item.cameraId !== camera.cameraId),
    );
    setSelectedCamera(null);
    void loadCameras();
  }

  const totalCameras = cameras.length;
  const onlineCameras = cameras.filter(
    (camera) =>
      isCameraEnabled(camera) && getCameraConnectionState(camera) === "online",
  ).length;
  const offlineCameras = cameras.filter(
    (camera) =>
      isCameraEnabled(camera) && getCameraConnectionState(camera) === "offline",
  ).length;
  const attentionCameras = cameras.filter(
    (camera) =>
      !isCameraEnabled(camera) || getCameraConnectionState(camera) !== "online",
  ).length;
  return (
    <div className="camera-page">
      <TopBar title="Qumra" onSignOut={onSignOut} email={email} />

      <div
        style={{
          width: "100%",
          maxWidth: 1800,
          margin: "0 auto",
          padding: "24px clamp(16px, 2vw, 32px)",
          boxSizing: "border-box",
        }}
      >
        <div style={pageHeaderStyle}>
          <div>
            <h1 style={{ marginBottom: 6 }}>Cameras</h1>

            <div
              style={{
                opacity: 0.8,
                marginBottom: 18,
              }}
            >
              Logged in as: <b>{email || "unknown"}</b>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <button
              type="button"
              onClick={() => void loadCameras()}
              disabled={loadingCameras}
              style={{
                ...btnOutline,
                ...(loadingCameras ? disabledButtonStyle : {}),
              }}
            >
              {loadingCameras ? "Refreshing..." : "Refresh Cameras"}
            </button>

            <button type="button" onClick={openModal} style={btnPrimary}>
              + Add Camera
            </button>
          </div>
        </div>

        <div style={cameraListContainerStyle}>
          <div style={cameraListHeaderStyle}>
            <div>
              <h2 style={{ margin: 0 }}>Camera List</h2>

              <div
                style={{
                  fontSize: 13,
                  opacity: 0.7,
                  marginTop: 5,
                }}
              >
                {loadingCameras
                  ? "Loading cameras..."
                  : `${cameras.length} camera${cameras.length === 1 ? "" : "s"}`}
              </div>
            </div>
          </div>
          {cameraListError && (
            <div style={errorMessageStyle}>
              <div style={{ fontWeight: 800 }}>Failed to load cameras</div>

              <div style={{ marginTop: 5 }}>{cameraListError}</div>

              <button
                type="button"
                onClick={() => void loadCameras()}
                style={{
                  ...btnOutline,
                  marginTop: 12,
                }}
              >
                Try Again
              </button>
            </div>
          )}

          {!cameraListError && (
            <>
              <div style={summaryGridStyle}>
                <SummaryCard
                  label="Total Cameras"
                  value={totalCameras}
                  description="All registered devices"
                  accent="#2563eb"
                  symbol="C"
                />

                <SummaryCard
                  label="Online"
                  value={onlineCameras}
                  description="Currently connected"
                  accent="#16a34a"
                  symbol="O"
                />

                <SummaryCard
                  label="Offline"
                  value={offlineCameras}
                  description="Not connected"
                  accent="#dc2626"
                  symbol="F"
                />

                <SummaryCard
                  label="Needs Attention"
                  value={attentionCameras}
                  description="Check camera connection"
                  accent="#d97706"
                  symbol="!"
                />
              </div>

              <CameraTable
                cameras={cameras}
                loading={loadingCameras}
                onCameraClick={openCameraDetails}
              />
            </>
          )}
        </div>
      </div>

      {showAddCamera && (
        <div style={modalOverlay} onMouseDown={closeModal}>
          <div
            style={modalCard}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div style={modalHeaderStyle}>
              <h2 style={{ margin: 0 }}>Add Camera</h2>

              <button
                type="button"
                onClick={closeModal}
                disabled={creating || loadingProfiles}
                style={{
                  ...btnX,
                  ...(creating || loadingProfiles ? disabledButtonStyle : {}),
                }}
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <div style={{ marginTop: 14 }}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  gap: 8,
                  padding: 4,
                  marginBottom: 18,
                  background: "#f1f5f9",
                  borderRadius: 10,
                }}
              >
                <button
                  type="button"
                  onClick={() => {
                    setConnectionMethod("direct");
                    setStatus("");
                  }}
                  style={{
                    border: 0,
                    borderRadius: 8,
                    padding: "10px 12px",
                    fontWeight: 800,
                    cursor: "pointer",
                    background: connectionMethod === "direct" ? "#ffffff" : "transparent",
                    color: connectionMethod === "direct" ? "#0f172a" : "#64748b",
                    boxShadow:
                      connectionMethod === "direct"
                        ? "0 1px 3px rgba(15, 23, 42, 0.12)"
                        : "none",
                  }}
                >
                  Direct / Port Forwarding
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setConnectionMethod("gateway");
                    setStatus("");
                  }}
                  style={{
                    border: 0,
                    borderRadius: 8,
                    padding: "10px 12px",
                    fontWeight: 800,
                    cursor: "pointer",
                    background: connectionMethod === "gateway" ? "#ffffff" : "transparent",
                    color: connectionMethod === "gateway" ? "#0f172a" : "#64748b",
                    boxShadow:
                      connectionMethod === "gateway"
                        ? "0 1px 3px rgba(15, 23, 42, 0.12)"
                        : "none",
                  }}
                >
                  Gateway
                </button>
              </div>

              {connectionMethod === "gateway" ? (
                <GatewayCameraOnboardingForm
                  onCameraCreated={async () => {
                    await loadCameras();
                  }}
                />
              ) : (
                <>
              <div style={twoColumnGridStyle}>
                <Field label="Camera public IP / host *">
                  <input
                    value={host}
                    onChange={(event) => setHost(event.target.value)}
                    placeholder="camera.example.com"
                    style={inputStyle}
                  />

                  <div style={fieldHintStyle}>
                    Normalized: <code>{normalizedHost || "-"}</code>
                  </div>
                </Field>

                <Field label="HTTP port *">
                  <input
                    value={httpPort}
                    onChange={(event) =>
                      setHttpPort(Number(event.target.value))
                    }
                    type="number"
                    min={1}
                    max={65535}
                    style={inputStyle}
                  />
                </Field>
              </div>

              <div style={twoColumnGridStyle}>
                <Field label="RTSP port *">
                  <input
                    value={rtspPort}
                    onChange={(event) =>
                      setRtspPort(Number(event.target.value))
                    }
                    type="number"
                    min={1}
                    max={65535}
                    style={inputStyle}
                  />
                </Field>

                <Field label="Archive hours">
                  <input
                    value={archiveHours}
                    onChange={(event) =>
                      setArchiveHours(Number(event.target.value))
                    }
                    type="number"
                    min={0}
                    style={inputStyle}
                  />
                </Field>
              </div>

              <div style={twoColumnGridStyle}>
                <Field label="ONVIF username *">
                  <input
                    value={username}
                    onChange={(event) => setUsername(event.target.value)}
                    autoComplete="username"
                    style={inputStyle}
                  />
                </Field>

                <Field label="ONVIF password *">
                  <input
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    type="password"
                    autoComplete="current-password"
                    style={inputStyle}
                  />
                </Field>
              </div>

              <div style={profileRowStyle}>
                <Field label="ONVIF profile *">
                  <select
                    value={selectedProfileToken}
                    onChange={(event) =>
                      setSelectedProfileToken(event.target.value)
                    }
                    style={inputStyle}
                    disabled={profiles.length === 0 || loadingProfiles}
                  >
                    {profiles.length === 0 ? (
                      <option value="">Click “Get Channels”</option>
                    ) : (
                      profiles.map((profile) => (
                        <option key={profile.token} value={profile.token}>
                          {profile.name}
                        </option>
                      ))
                    )}
                  </select>
                </Field>

                <button
                  type="button"
                  onClick={() => void getChannels()}
                  disabled={loadingProfiles || creating}
                  style={{
                    ...btnOutline,
                    marginBottom: 12,
                    ...(loadingProfiles || creating ? disabledButtonStyle : {}),
                  }}
                >
                  {loadingProfiles ? "Getting..." : "Get Channels"}
                </button>
              </div>

              {(portalRole === "INTEGRATOR" || portalRole === "ADMIN") && (
                <Field label={portalRole === "ADMIN" ? "Access Role *" : "Access Role"}>
                  <select
                    value={selectedAccessRoleId}
                    onChange={(event) => setSelectedAccessRoleId(event.target.value)}
                    style={inputStyle}
                  >
                    {portalRole === "INTEGRATOR" && <option value="">Unassigned for now</option>}
                    {accessRoles.length === 0 && portalRole === "ADMIN" ? (
                      <option value="">No Access Roles assigned</option>
                    ) : (
                      accessRoles.map((accessRole) => (
                        <option key={accessRole.accessRoleId} value={accessRole.accessRoleId}>
                          {accessRole.name}
                        </option>
                      ))
                    )}
                  </select>
                  <div style={fieldHintStyle}>
                    {portalRole === "ADMIN"
                      ? "You can choose only Access Roles assigned by your Integrator."
                      : "Optional now; cameras can also be grouped later from Access Roles."}
                  </div>
                </Field>
              )}

              <Field label="Camera name *">
                <input
                  value={cameraName}
                  onChange={(event) => setCameraName(event.target.value)}
                  placeholder="Front Door Camera"
                  style={inputStyle}
                />
              </Field>

              <div style={modalActionsStyle}>
                <button
                  type="button"
                  onClick={() => void createCamera()}
                  disabled={creating || loadingProfiles}
                  style={{
                    ...btnPrimary,
                    ...(creating || loadingProfiles ? disabledButtonStyle : {}),
                  }}
                >
                  {creating ? "Creating..." : "Create Camera"}
                </button>

                <button
                  type="button"
                  onClick={closeModal}
                  disabled={creating || loadingProfiles}
                  style={{
                    ...btnOutline,
                    ...(creating || loadingProfiles ? disabledButtonStyle : {}),
                  }}
                >
                  Cancel
                </button>
              </div>

              {status && (
                <div style={statusContainerStyle}>
                  <div
                    style={{
                      fontWeight: 800,
                      marginBottom: 6,
                    }}
                  >
                    {status.startsWith("✅") ? "Success" : "Note / Error"}
                  </div>

                  <div
                    style={{
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {status}
                  </div>
                </div>
              )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {selectedCamera && (
        <CameraDetailsModal
          camera={selectedCamera}
          onClose={closeCameraDetails}
          onUpdateCamera={updateCameraDetails}
          canDelete={canDeleteCameras}
          onDeleteCamera={deleteCamera}
        />
      )}
    </div>
  );
}
function SummaryCard({
  label,
  value,
  description,
  accent,
  symbol,
}: {
  label: string;
  value: number;
  description: string;
  accent: string;
  symbol: string;
}) {
  return (
    <div style={summaryCardStyle}>
      <div>
        <div style={summaryLabelStyle}>{label}</div>

        <div style={summaryValueStyle}>{value}</div>

        <div style={summaryDescriptionStyle}>{description}</div>
      </div>

      <div
        style={{
          ...summaryIconStyle,
          color: accent,
          backgroundColor: `${accent}14`,
          borderColor: `${accent}30`,
        }}
      >
        {symbol}
      </div>
    </div>
  );
}
function CameraTable({
  cameras,
  loading,
  onCameraClick,
}: {
  cameras: Camera[];
  loading: boolean;
  onCameraClick: (camera: Camera) => void;
}) {
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "online" | "offline" | "disabled"
  >("all");
  const filteredCameras = cameras.filter((camera) => {
    const connectionState = getCameraConnectionState(camera);
    const enabled = isCameraEnabled(camera);
    const matchesStatus =
      statusFilter === "all" ||
      (statusFilter === "disabled"
        ? !enabled
        : enabled && statusFilter === connectionState);
    const searchableText = [
      camera.name,
      camera.cameraId,
      camera.registrationCode,
      camera.assignedTo,
      camera.customerId,
      camera.integratorName,
      camera.accessRoleName,
      camera.cameraAddedByName,
      camera.host,
      camera.planName,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    const matchesSearch = searchableText.includes(query.trim().toLowerCase());
    return matchesStatus && matchesSearch;
  });
  return (
    <section style={fleetPanelStyle}>
      <div style={fleetHeaderStyle}>
        <div>
          <div style={fleetTitleStyle}>Camera Fleet</div>

          <div style={fleetSubtitleStyle}>
            View and manage your registered camera devices
          </div>
        </div>

        <div style={fleetResultCountStyle}>
          {filteredCameras.length} device
          {filteredCameras.length === 1 ? "" : "s"}
        </div>
      </div>

      <div style={fleetToolbarStyle}>
        <div style={fleetSearchWrapperStyle}>
          <span style={fleetSearchIconStyle}>⌕</span>

          <input
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search by camera ID, customer or code..."
            style={fleetSearchInputStyle}
          />
        </div>

        <div style={fleetFiltersStyle}>
          <button
            type="button"
            onClick={() => setStatusFilter("all")}
            style={{
              ...fleetFilterButtonStyle,
              ...(statusFilter === "all" ? fleetFilterButtonActiveStyle : {}),
            }}
          >
            All
          </button>

          <button
            type="button"
            onClick={() => setStatusFilter("online")}
            style={{
              ...fleetFilterButtonStyle,
              ...(statusFilter === "online"
                ? fleetFilterButtonActiveStyle
                : {}),
            }}
          >
            Online
          </button>

          <button
            type="button"
            onClick={() => setStatusFilter("offline")}
            style={{
              ...fleetFilterButtonStyle,
              ...(statusFilter === "offline"
                ? fleetFilterButtonActiveStyle
                : {}),
            }}
          >
            Offline
          </button>

          <button
            type="button"
            onClick={() => setStatusFilter("disabled")}
            style={{
              ...fleetFilterButtonStyle,
              ...(statusFilter === "disabled"
                ? fleetFilterButtonActiveStyle
                : {}),
            }}
          >
            Disabled
          </button>
        </div>
      </div>

      <div style={fleetListStyle}>
        {loading && <div style={fleetEmptyStyle}>Loading camera fleet...</div>}

        {!loading && filteredCameras.length === 0 && (
          <div style={fleetEmptyStyle}>
            <div style={fleetEmptyIconStyle}>⌕</div>

            <div style={fleetEmptyTitleStyle}>No matching cameras</div>

            <div style={fleetEmptyDescriptionStyle}>
              Try changing the search or status filter.
            </div>
          </div>
        )}

        {!loading &&
          filteredCameras.map((camera) => {
            const connectionState = getCameraConnectionState(camera);
            const enabled = isCameraEnabled(camera);
            const disabled = !enabled;
            const connected = enabled && connectionState === "online";
            const offline = enabled && connectionState === "offline";
            const customerName =
              camera.assignedTo?.split("\n")[0]?.trim() ||
              camera.customerId ||
              "Unassigned";
            return (
              <article
                key={camera.cameraId}
                style={fleetCameraRowStyle}
                role="button"
                tabIndex={0}
                onClick={() => onCameraClick(camera)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onCameraClick(camera);
                  }
                }}
              >
                <div
                  style={{
                    ...fleetStatusRailStyle,
                    background: disabled
                      ? "#64748b"
                      : connected
                        ? "#22c55e"
                        : offline
                          ? "#ef4444"
                          : "#f59e0b",
                  }}
                />

                <div style={fleetCameraIdentityStyle}>
                  {camera.thumbnailUrl ? (
                    <img
                      src={camera.thumbnailUrl}
                      alt={camera.name}
                      loading="lazy"
                      decoding="async"
                      style={fleetThumbnailStyle}
                    />
                  ) : (
                    <div
                      style={{
                        ...fleetThumbnailPlaceholderStyle,
                        background: disabled
                          ? "linear-gradient(135deg, #f1f5f9, #cbd5e1)"
                          : connected
                            ? "linear-gradient(135deg, #dbeafe, #bfdbfe)"
                            : offline
                              ? "linear-gradient(135deg, #fee2e2, #fecaca)"
                              : "linear-gradient(135deg, #fef3c7, #fde68a)",
                      }}
                    >
                      <span style={fleetCameraIconStyle}>◉</span>
                    </div>
                  )}

                  <div style={fleetCameraNameWrapperStyle}>
                    <div style={fleetCameraNameStyle}>
                      {camera.name || `Camera ${camera.cameraId}`}
                    </div>

                    <div style={fleetCameraIdStyle}>ID #{camera.cameraId}</div>
                  </div>
                </div>

                <div style={fleetConnectionStyle}>
                  <span
                    style={{
                      ...fleetStatusPillStyle,
                      ...(disabled
                        ? fleetDisabledPillStyle
                        : connected
                          ? fleetOnlinePillStyle
                          : offline
                            ? fleetOfflinePillStyle
                            : fleetUnknownPillStyle),
                    }}
                  >
                    <span
                      style={{
                        ...fleetStatusDotStyle,
                        background: disabled
                          ? "#64748b"
                          : connected
                            ? "#16a34a"
                            : offline
                              ? "#dc2626"
                              : "#d97706",
                      }}
                    />

                    {disabled
                      ? "Disabled"
                      : connected
                        ? "Online"
                        : offline
                          ? "Offline"
                          : "Unknown"}
                  </span>

                  <span style={fleetConnectionTextStyle}>
                    {disabled
                      ? "Playback blocked"
                      : connected
                        ? "Camera connected"
                        : offline
                          ? "Connection required"
                          : "Status unavailable"}
                  </span>
                </div>

                <div style={fleetDetailStyle}>
                  <span style={fleetDetailLabelStyle}>Registration</span>

                  <span style={fleetDetailValueStyle}>
                    {camera.registrationCode || "Not assigned"}
                  </span>
                </div>

                <div style={fleetDetailStyle}>
                  <span style={fleetDetailLabelStyle}>Customer</span>

                  <span style={fleetDetailValueStyle} title={camera.assignedTo}>
                    {customerName}
                  </span>
                </div>


                <div style={fleetDetailStyle}>
                  <span style={fleetDetailLabelStyle}>Integrator</span>
                  <span style={fleetDetailValueStyle}>
                    {camera.integratorName || customerName}
                  </span>
                </div>

                <div style={fleetDetailStyle}>
                  <span style={fleetDetailLabelStyle}>Access Role</span>
                  <span style={fleetDetailValueStyle}>
                    {camera.accessRoleName || "Unassigned"}
                  </span>
                </div>

                <div style={fleetDetailStyle}>
                  <span style={fleetDetailLabelStyle}>Added by</span>
                  <span style={fleetDetailValueStyle}>
                    {camera.cameraAddedByName
                      ? camera.cameraAddedByName + (camera.cameraAddedByRole ? " (" + camera.cameraAddedByRole + ")" : "")
                      : "Legacy / unknown"}
                  </span>
                </div>

                <div style={fleetDetailStyle}>
                  <span style={fleetDetailLabelStyle}>Plan</span>

                  <span style={fleetPlanBadgeStyle}>
                    {camera.planName || "No plan"}
                  </span>
                </div>

                <div style={fleetDetailStyle}>
                  <span style={fleetDetailLabelStyle}>Last updated</span>

                  <span style={fleetDetailValueStyle}>
                    {formatDate(camera.updatedAt)}
                  </span>
                </div>

                <button
                  type="button"
                  title="Open camera details"
                  style={fleetActionButtonStyle}
                  onClick={(event) => {
                    event.stopPropagation();
                    onCameraClick(camera);
                  }}
                >
                  •••
                </button>
              </article>
            );
          })}
      </div>
    </section>
  );
}

function CameraDetailsModal({
  camera,
  onClose,
  onUpdateCamera,
  canDelete,
  onDeleteCamera,
}: {
  camera: Camera;
  onClose: () => void;
  onUpdateCamera: (camera: Camera) => void;
  canDelete: boolean;
  onDeleteCamera: (camera: Camera) => Promise<void>;
}) {
  const [activeTab, setActiveTab] = useState<CameraDetailsTab>("info");
  const [showGeneralInfoDetails, setShowGeneralInfoDetails] = useState(false);
  const [details, setDetails] = useState<CameraDeviceDetails>(() =>
    createCameraDeviceDetails(camera),
  );
  const [enabledDraft, setEnabledDraft] = useState(() =>
    isCameraEnabled(camera),
  );
  const [generalStatus, setGeneralStatus] = useState("");
  const [generalStatusIsError, setGeneralStatusIsError] = useState(false);
  const [savingGeneral, setSavingGeneral] = useState(false);
  const [finalizingGateway, setFinalizingGateway] = useState(false);
  const finalizingGatewayRef = useRef(false);
  const gatewayFinalizationRunRef = useRef(0);
  const gatewayFinalizationAbortRef = useRef<AbortController | null>(null);
  const savingGeneralRef = useRef(false);
  const [supportStatus, setSupportStatus] = useState("");
  const [remoteSupportSession, setRemoteSupportSession] =
    useState<RemoteSupportSession | null>(null);
  const [creatingRemoteSupport, setCreatingRemoteSupport] = useState(false);
  const [revokingRemoteSupport, setRevokingRemoteSupport] = useState(false);
  const [editingSupportCredentials, setEditingSupportCredentials] =
    useState(false);
  const [qualityProfiles, setQualityProfiles] = useState<OnvifQualityProfile[]>(
    [],
  );
  const [selectedQualityToken, setSelectedQualityToken] = useState("");
  const [qualityDraft, setQualityDraft] = useState<OnvifQualityProfile | null>(
    null,
  );
  const [loadingQuality, setLoadingQuality] = useState(false);
  const [qualityStatus, setQualityStatus] = useState("");
  const [qualitySource, setQualitySource] = useState<"onvif" | "demo" | "">("");
  const [deletingCamera, setDeletingCamera] = useState(false);
  const [deleteStatus, setDeleteStatus] = useState("");
  const [thumbnailAvailable, setThumbnailAvailable] = useState(false);

  useEffect(() => {
    setDetails(createCameraDeviceDetails(camera));
    setEnabledDraft(isCameraEnabled(camera));
    setActiveTab("info");
    setShowGeneralInfoDetails(false);
    setGeneralStatus("");
    setGeneralStatusIsError(false);
    setSavingGeneral(false);
    setFinalizingGateway(false);
    gatewayFinalizationRunRef.current += 1;
    // Cancel any request owned by the previously selected camera.
    gatewayFinalizationAbortRef.current?.abort();
    gatewayFinalizationAbortRef.current = null;
    finalizingGatewayRef.current = false;
    savingGeneralRef.current = false;
    setSupportStatus("");
    setRemoteSupportSession(null);
    setCreatingRemoteSupport(false);
    setRevokingRemoteSupport(false);
    setEditingSupportCredentials(false);
    setQualityProfiles([]);
    setSelectedQualityToken("");
    setQualityDraft(null);
    setQualityStatus("");
    setQualitySource("");
    setThumbnailAvailable(false);
  }, [camera.cameraId]);


  useEffect(() => {
    return () => {
      // Cancel Gateway polling when camera details close.
      gatewayFinalizationRunRef.current += 1;
      gatewayFinalizationAbortRef.current?.abort();
      gatewayFinalizationAbortRef.current = null;
      finalizingGatewayRef.current = false;
    };
  }, []);

  const selectedConnectionState = getCameraConnectionState(camera);
  const cameraEnabled = isCameraEnabled(camera);
  const pendingGatewayOnboarding =
    camera.connectionType === "gateway" &&
    Boolean(camera.onboardingStatus) &&
    camera.onboardingStatus !== "READY";
  const connectionMethod = getCameraConnectionMethod(camera);
  const isOnline =
    cameraEnabled && (thumbnailAvailable || selectedConnectionState === "online");

  function applyReadyGatewayCamera(
    finalizedCamera: Camera,
    profileName = "",
  ) {
    onUpdateCamera(finalizedCamera);
    setDetails(createCameraDeviceDetails(finalizedCamera));
    setEnabledDraft(false);
    setGeneralStatusIsError(false);
    setGeneralStatus(
      profileName
        ? `Gateway setup complete. H.264 profile ${profileName} was validated and the camera is ready to enable.`
        : "Gateway setup complete. The secure tunnel and H.264 profile were validated and the camera is ready to enable.",
    );
  }

  async function pollGatewayCameraUntilReady(
    idToken: string,
    initialDelaySeconds: number,
    runId: number,
    signal: AbortSignal,
  ): Promise<Camera | null> {
    const deadline = Date.now() + 35 * 60 * 1000;
    let delaySeconds = initialDelaySeconds;

    while (Date.now() < deadline) {
      await new Promise<void>((resolve) => {
        let completed = false;
        let timeoutId: number | undefined;
        const finish = () => {
          if (completed) return;
          completed = true;
          if (timeoutId !== undefined) window.clearTimeout(timeoutId);
          signal.removeEventListener("abort", finish);
          resolve();
        };
        timeoutId = window.setTimeout(finish, delaySeconds * 1000);
        signal.addEventListener("abort", finish, { once: true });
        if (signal.aborted) finish();
      });
      if (signal.aborted || gatewayFinalizationRunRef.current !== runId) {
        return null;
      }

      let latest: Camera | null = null;
      try {
        const response = await fetch(CAMERAS_API, {
          method: "GET",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          cache: "no-store",
          signal,
        });
        if (signal.aborted || gatewayFinalizationRunRef.current !== runId) {
          return null;
        }
        const { text, json } = await readResponse(response);
        if (signal.aborted || gatewayFinalizationRunRef.current !== runId) {
          return null;
        }
        if (!response.ok) {
          throw new Error(
            `${response.status}: ${json?.message || text || response.statusText || "Unable to refresh camera setup status"}`,
          );
        }
        latest = findGatewayCamera(json, camera.cameraId) as Camera | null;
        if (!latest) {
          throw new Error("Camera was not present in the refreshed camera list.");
        }
      } catch (error) {
        if (signal.aborted || gatewayFinalizationRunRef.current !== runId) {
          return null;
        }
        console.warn("Gateway finalization status refresh failed", getErrorMessage(error));
        delaySeconds = 15;
        continue;
      }

      if (!latest) continue;
      onUpdateCamera(latest);
      if (latest.onboardingStatus === "READY") return latest;

      const failure = gatewayFinalizationFailure(latest);
      if (failure) throw new Error(failure);

      setGeneralStatusIsError(false);
      setGeneralStatus(
        `AWS Media Gateway setup is still running. Current state: ${latest.onboardingStatus || "FINALIZING"}. This page will update automatically.`,
      );
      delaySeconds = 15;
    }

    return null;
  }

  async function finalizeGatewayCamera() {
    if (
      !pendingGatewayOnboarding ||
      finalizingGatewayRef.current ||
      savingGeneralRef.current
    ) {
      return;
    }

    finalizingGatewayRef.current = true;
    const runId = gatewayFinalizationRunRef.current + 1;
    gatewayFinalizationRunRef.current = runId;
    gatewayFinalizationAbortRef.current?.abort();
    const abortController = new AbortController();
    gatewayFinalizationAbortRef.current = abortController;
    setFinalizingGateway(true);
    setGeneralStatusIsError(false);
    setGeneralStatus("Opening secure Gateway tunnel and validating ONVIF/H.264...");

    try {
      const idToken = await getIdTokenOrThrow();
      if (
        abortController.signal.aborted ||
        gatewayFinalizationRunRef.current !== runId
      ) {
        return;
      }
      const response = await fetch(
        `${CAMERAS_API}/${encodeURIComponent(camera.cameraId)}/gateway-finalize`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({}),
          signal: abortController.signal,
        },
      );
      if (
        abortController.signal.aborted ||
        gatewayFinalizationRunRef.current !== runId
      ) {
        return;
      }
      const { text, json } = await readResponse(response);
      if (
        abortController.signal.aborted ||
        gatewayFinalizationRunRef.current !== runId
      ) {
        return;
      }
      if (!response.ok) {
        throw new Error(
          `${response.status}: ${json?.message || text || response.statusText || "Gateway finalization failed"}`,
        );
      }

      const result = classifyGatewayFinalizationResponse(response.status, json);
      if (result.state === "ready") {
        applyReadyGatewayCamera(
          result.camera as Camera,
          json?.profile?.profileName || json?.profile?.profileToken || "selected",
        );
        return;
      }

      setGeneralStatusIsError(false);
      setGeneralStatus(
        `${result.message} Waiting for camera ${camera.cameraId}; this page will update automatically.`,
      );
      const finalizedCamera = await pollGatewayCameraUntilReady(
        idToken,
        result.retryAfterSeconds,
        runId,
        abortController.signal,
      );
      if (
        abortController.signal.aborted ||
        gatewayFinalizationRunRef.current !== runId
      ) {
        return;
      }
      if (finalizedCamera) {
        applyReadyGatewayCamera(finalizedCamera);
      } else {
        setGeneralStatusIsError(false);
        setGeneralStatus(
          "Gateway setup is still pending. It will continue in the background; refresh the camera later to see its latest status.",
        );
      }
    } catch (error) {
      if (
        abortController.signal.aborted ||
        gatewayFinalizationRunRef.current !== runId
      ) {
        return;
      }
      setGeneralStatusIsError(true);
      setGeneralStatus(`Unable to complete Gateway setup: ${getErrorMessage(error)}`);
    } finally {
      if (gatewayFinalizationRunRef.current === runId) {
        if (gatewayFinalizationAbortRef.current === abortController) {
          gatewayFinalizationAbortRef.current = null;
        }
        finalizingGatewayRef.current = false;
        setFinalizingGateway(false);
      }
    }
  }

  async function loadQuality() {
    setLoadingQuality(true);
    setQualityStatus(
      "Connecting to camera and reading ONVIF encoder settings...",
    );

    try {
      const result = await readOnvifQuality(camera);
      const loadedProfiles = cloneQualityProfiles(result.profiles);
      const firstProfile = loadedProfiles[0];

      setQualityProfiles(loadedProfiles);
      setSelectedQualityToken(firstProfile?.token || "");
      setQualityDraft(firstProfile ? { ...firstProfile } : null);
      setQualitySource(result.source);
      setQualityStatus(result.message);
      if (result.deviceDetails) {
        setDetails((current) => ({
          ...current,
          ...result.deviceDetails,
        }));
      }
    } catch (error) {
      setQualityProfiles([]);
      setQualityDraft(null);
      setQualitySource("");
      setQualityStatus(
        error instanceof Error
          ? error.message
          : "Unable to read ONVIF quality settings.",
      );
    } finally {
      setLoadingQuality(false);
    }
  }

  function selectQualityProfile(token: string) {
    const profile = qualityProfiles.find((item) => item.token === token);
    setSelectedQualityToken(token);
    setQualityDraft(profile ? { ...profile } : null);
    setQualityStatus("");
  }

  function updateQualityDraft(changes: Partial<OnvifQualityProfile>) {
    setQualityDraft((current) =>
      current
        ? {
            ...current,
            ...changes,
          }
        : current,
    );
  }

  function changeEnabledDraft(nextEnabled: boolean) {
    if (
      !nextEnabled &&
      enabledDraft &&
      !window.confirm(
        "Disable this camera? Live video and new cloud recording will stop. Previously recorded footage will remain available until its normal retention period expires.",
      )
    ) {
      return;
    }

    setEnabledDraft(nextEnabled);
    setGeneralStatus("");
    setGeneralStatusIsError(false);
  }

  async function saveGeneral() {
    if (savingGeneralRef.current) return;

    const enabledChanged = enabledDraft !== cameraEnabled;
    const currentMotionEventsEnabled = isMotionEventsEnabled(camera);
    const motionEventsChanged =
      details.motionEvents !== currentMotionEventsEnabled;
    savingGeneralRef.current = true;
    setGeneralStatusIsError(false);
    setSavingGeneral(true);
    setGeneralStatus(
      enabledChanged && motionEventsChanged
        ? "Saving camera and motion-event settings..."
        : enabledChanged
        ? `${enabledDraft ? "Enabling" : "Disabling"} camera...`
        : motionEventsChanged
        ? `${details.motionEvents ? "Enabling" : "Disabling"} motion events...`
        : "Saving camera general settings...",
    );

    let stateUpdateWarning = "";
    let motionUpdateWarning = "";
    let settingsReconciled = false;

    try {
      const idToken =
        enabledChanged || motionEventsChanged
          ? await getIdTokenOrThrow()
          : "";

      const patchCameraSetting = async (
        suffix: string,
        body: Record<string, boolean>,
        fallbackMessage: string,
      ) => {
        const response = await fetch(
          `${CAMERAS_API}/${encodeURIComponent(camera.cameraId)}/${suffix}`,
          {
            method: "PATCH",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
              Authorization: `Bearer ${idToken}`,
            },
            body: JSON.stringify(body),
          },
        );
        const { text, json } = await readResponse(response);
        if (!response.ok) {
          throw new Error(
            `${response.status}: ${
              json?.message || text || response.statusText || fallbackMessage
            }`,
          );
        }
        return json;
      };

      const [stateResult, motionResult] = await Promise.allSettled([
        enabledChanged
          ? patchCameraSetting(
              "state",
              { enabled: enabledDraft },
              "Unable to update camera state.",
            )
          : Promise.resolve(null),
        motionEventsChanged
          ? patchCameraSetting(
              "motion-events",
              { motionEventsEnabled: details.motionEvents },
              "Unable to update motion-event collection.",
            )
          : Promise.resolve(null),
      ]);

      const savedEnabled =
        !enabledChanged || stateResult.status === "fulfilled";
      const savedMotionEvents =
        !motionEventsChanged || motionResult.status === "fulfilled";
      const motionResponse =
        motionResult.status === "fulfilled" ? motionResult.value : null;
      const persistedEnabled =
        enabledChanged && savedEnabled ? enabledDraft : cameraEnabled;
      const persistedMotionEvents =
        motionEventsChanged && savedMotionEvents
          ? typeof motionResponse?.motionEventsEnabled === "boolean"
            ? motionResponse.motionEventsEnabled
            : details.motionEvents
          : currentMotionEventsEnabled;
      const persistedMotionStatus =
        motionEventsChanged && savedMotionEvents
          ? motionResponse?.motionEventsStatus ||
            (persistedMotionEvents ? "PENDING" : "DISABLED")
          : camera.motionEventsStatus;

      if (stateResult.status === "fulfilled" && stateResult.value?.warning) {
        stateUpdateWarning =
          stateResult.value?.message ||
          "Camera state was saved, but live-path synchronization needs attention.";
      }
      if (motionResult.status === "fulfilled" && motionResult.value?.warning) {
        motionUpdateWarning =
          motionResult.value?.message ||
          "Motion-event setting was saved, but the closing transition needs attention.";
      }

      const updatedCamera: Camera = {
        ...camera,
        name: details.description.trim() ? camera.name : camera.name,
        enabled: persistedEnabled,
        ...(motionEventsChanged
          ? {
              motionEventsEnabled: persistedMotionEvents,
              motionEventsStatus: persistedMotionStatus,
            }
          : {}),
        updatedAt: new Date().toISOString(),
        deviceDetails: {
          ...details,
          motionEvents: persistedMotionEvents,
        },
      };

      const cameraNameInput = document.getElementById(
        `camera-name-${camera.cameraId}`,
      ) as HTMLInputElement | null;

      if (cameraNameInput?.value.trim()) {
        updatedCamera.name = cameraNameInput.value.trim();
      }

      if (enabledChanged && !savedEnabled) setEnabledDraft(cameraEnabled);
      if (motionEventsChanged) {
        setDetails((current) => ({
          ...current,
          motionEvents: persistedMotionEvents,
        }));
      }
      onUpdateCamera(updatedCamera);
      settingsReconciled = true;

      const failedMessages = [
        stateResult.status === "rejected"
          ? `camera state: ${getErrorMessage(stateResult.reason)}`
          : "",
        motionResult.status === "rejected"
          ? `motion events: ${getErrorMessage(motionResult.reason)}`
          : "",
      ].filter(Boolean);

      if (failedMessages.length) {
        throw new Error(`Not all settings were saved (${failedMessages.join("; ")})`);
      }

      const warnings = [stateUpdateWarning, motionUpdateWarning].filter(Boolean);
      setGeneralStatus(
        warnings.length
          ? `Settings saved. Warning: ${warnings.join(" ")}`
          : enabledChanged && motionEventsChanged
          ? "Camera and motion-event settings saved successfully."
          : enabledChanged
          ? `Camera ${enabledDraft ? "enabled" : "disabled"} successfully.`
          : motionEventsChanged
          ? `Motion events ${details.motionEvents ? "enabled" : "disabled"} successfully.`
          : "Camera general settings saved.",
      );
    } catch (error) {
      if (!settingsReconciled) {
        if (enabledChanged) setEnabledDraft(cameraEnabled);
        if (motionEventsChanged) {
          setDetails((current) => ({
            ...current,
            motionEvents: currentMotionEventsEnabled,
          }));
        }
      }
      setGeneralStatusIsError(true);
      setGeneralStatus(`Unable to save camera: ${getErrorMessage(error)}`);
    } finally {
      savingGeneralRef.current = false;
      setSavingGeneral(false);
    }
  }

  async function requestDeleteCamera() {
    if (deletingCamera) return;
    const confirmed = window.confirm(
      `Permanently delete "${camera.name || ("Camera " + camera.cameraId)}"?\n\nThis permanently removes the camera, all archive recordings, motion and analytics history, retention settings, Gateway registration data, credentials, thumbnails, gateway files, and user assignments. Billing and invoice history are retained. This cannot be undone.`,
    );
    if (!confirmed) return;

    setDeletingCamera(true);
    setDeleteStatus("Starting permanent camera purge...");
    try {
      await onDeleteCamera(camera);
    } catch (error) {
      setDeleteStatus(`Unable to delete camera: ${getErrorMessage(error)}`);
      setDeletingCamera(false);
    }
  }

  async function createRemoteSupportSession(
    targetType: "camera" | "gateway",
  ) {
    if (creatingRemoteSupport) return;
    if (camera.connectionType !== "gateway") {
      setSupportStatus(
        "Remote WebUI access is available only for cameras connected through a physical Gateway.",
      );
      return;
    }
    if (targetType === "gateway" && !camera.edgeGatewayId) {
      setSupportStatus("This camera has no physical Gateway identity.");
      return;
    }

    setCreatingRemoteSupport(true);
    setSupportStatus(
      targetType === "camera"
        ? "Creating a 15-minute camera WebUI support link..."
        : "Creating a 15-minute Gateway WebUI support link...",
    );
    try {
      const idToken = await getIdTokenOrThrow();
      const response = await fetch(SUPPORT_SESSIONS_API, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify(
          targetType === "camera"
            ? { targetType: "camera", cameraId: camera.cameraId }
            : { targetType: "gateway", gatewayId: camera.edgeGatewayId },
        ),
      });
      const { text, json } = await readResponse(response);
      if (!response.ok) {
        throw new Error(
          json?.message ||
            text ||
            `Unable to create remote support link (${response.status}).`,
        );
      }
      const session = json as RemoteSupportSession;
      if (!session?.sessionId || !session?.url || !session?.expiresAt) {
        throw new Error("Remote support API returned an incomplete session.");
      }
      setRemoteSupportSession(session);
      setSupportStatus(
        `${targetType === "camera" ? "Camera" : "Gateway"} remote access is ready until ${formatDate(session.expiresAt)}.`,
      );
    } catch (error) {
      setRemoteSupportSession(null);
      setSupportStatus(`Unable to create remote access: ${getErrorMessage(error)}`);
    } finally {
      setCreatingRemoteSupport(false);
    }
  }

  async function revokeRemoteSupportSession() {
    const session = remoteSupportSession;
    if (!session || revokingRemoteSupport) return;
    setRevokingRemoteSupport(true);
    setSupportStatus("Revoking remote support link...");
    try {
      const idToken = await getIdTokenOrThrow();
      const response = await fetch(
        `${SUPPORT_SESSIONS_API}/${encodeURIComponent(session.sessionId)}`,
        {
          method: "DELETE",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${idToken}`,
          },
        },
      );
      const { text, json } = await readResponse(response);
      if (!response.ok) {
        throw new Error(
          json?.message ||
            text ||
            `Unable to revoke support link (${response.status}).`,
        );
      }
      setRemoteSupportSession(null);
      setSupportStatus("Remote support link revoked.");
    } catch (error) {
      setSupportStatus(`Unable to revoke remote access: ${getErrorMessage(error)}`);
    } finally {
      setRevokingRemoteSupport(false);
    }
  }

  function restartDemoCamera() {
    setSupportStatus(
      "Demo restart request recorded locally. No API call was made.",
    );
  }

  function saveSupportCredentials() {
    const updatedCamera: Camera = {
      ...camera,
      updatedAt: new Date().toISOString(),
      deviceDetails: details,
    };

    onUpdateCamera(updatedCamera);
    setEditingSupportCredentials(false);
    setSupportStatus(
      "Demo P&P credentials saved locally. No API call was made.",
    );
  }

  const generalInfoRows: Array<[string, React.ReactNode]> = [
    ["Camera ID", camera.cameraId],
    ["Camera name", camera.name],
    ["Customer ID", camera.customerId],
    ["Manufacturer", details.manufacturer],
    ["Model", details.model],
    ["Serial number", details.serialNumber],
    ["Enabled", cameraEnabled ? "On" : "Off"],
    ["Alerts", details.alerts ? "On" : "Off"],
    [
      "Cloud recording",
      details.cloudRecording ? camera.planName || "On" : "Off",
    ],
    ["Plan", camera.planName || "No plan"],
    ["Assigned to", camera.assignedTo || "Unassigned"],
    ["Registration code", camera.registrationCode || "Not assigned"],
    ["Connection method", connectionMethod],
  ];

  return (
    <div
      style={cameraDetailsOverlayStyle}
      onMouseDown={savingGeneral ? undefined : onClose}
    >
      <section
        style={cameraDetailsModalStyle}
        onMouseDown={(event) => event.stopPropagation()}
        aria-modal="true"
        role="dialog"
        aria-label={`${camera.name} details`}
      >
        <header style={cameraDetailsHeaderStyle}>
          <div style={cameraDetailsIdentityStyle}>
            {camera.thumbnailUrl ? (
              <img
                src={camera.thumbnailUrl}
                alt={camera.name}
                onLoad={() => setThumbnailAvailable(true)}
                onError={() => setThumbnailAvailable(false)}
                style={cameraDetailsThumbnailStyle}
              />
            ) : (
              <div style={cameraDetailsThumbnailPlaceholderStyle}>◉</div>
            )}

            <div>
              <div style={cameraDetailsTitleStyle}>{camera.name}</div>

              <div style={cameraTagRowStyle}>
                <StatusTag label="HTTP" tone="green" />
                <StatusTag label={connectionMethod} tone="green" />
                <StatusTag
                  label={
                    cameraEnabled
                      ? isOnline
                        ? "Video Online"
                        : "Video Offline"
                      : "Camera Disabled"
                  }
                  tone={isOnline ? "green" : "red"}
                />
                <StatusTag label="Cloud" tone="blue" />
                <StatusTag label="Cloud Analytics" tone="blue" />
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={savingGeneral}
            style={{
              ...cameraDetailsCloseStyle,
              ...(savingGeneral ? disabledButtonStyle : {}),
            }}
            aria-label="Close camera details"
          >
            ×
          </button>
        </header>

        <nav style={cameraDetailsTabsStyle}>
          <CameraTabButton
            active={activeTab === "info"}
            label="Info"
            onClick={() => setActiveTab("info")}
          />
          <CameraTabButton
            active={activeTab === "general"}
            label="General"
            onClick={() => setActiveTab("general")}
          />
          <CameraTabButton
            active={activeTab === "archive"}
            label="Archive"
            onClick={() => setActiveTab("archive")}
          />
          <CameraTabButton
            active={activeTab === "analytics"}
            label="Analytics"
            onClick={() => setActiveTab("analytics")}
          />
          <CameraTabButton
            active={activeTab === "onvif"}
            label="ONVIF Connection"
            onClick={() => setActiveTab("onvif")}
          />
          <CameraTabButton
            active={activeTab === "quality"}
            label="Quality"
            onClick={() => setActiveTab("quality")}
          />
          <CameraTabButton
            active={activeTab === "location"}
            label="Location"
            onClick={() => setActiveTab("location")}
          />
          <CameraTabButton
            active={activeTab === "billing"}
            label="Billing"
            onClick={() => setActiveTab("billing")}
          />
          <CameraTabButton
            active={activeTab === "support"}
            label="Diagnostics"
            onClick={() => setActiveTab("support")}
          />
        </nav>

        <div style={cameraDetailsBodyStyle}>
          {activeTab === "info" && (
            <div style={cameraInfoLayoutStyle}>
              {!showGeneralInfoDetails ? (
                <button
                  type="button"
                  onClick={() => setShowGeneralInfoDetails(true)}
                  style={generalInfoLaunchCardStyle}
                >
                  <div style={generalInfoLaunchIconStyle}>i</div>
                  <div style={{ minWidth: 0 }}>
                    <div style={generalInfoLaunchTitleStyle}>General Info</div>
                    <div style={generalInfoLaunchDescriptionStyle}>
                      View the selected camera information, availability,
                      analytics support, and the latest camera thumbnail.
                    </div>
                  </div>
                  <span style={generalInfoLaunchArrowStyle}>
                    View details →
                  </span>
                </button>
              ) : (
                <section style={cameraInfoCardStyle}>
                  <div style={generalInfoExpandedHeaderStyle}>
                    <div>
                      <div style={cameraInfoSectionTitleTextStyle}>
                        General Info
                      </div>
                      <div style={generalInfoExpandedSubtitleStyle}>
                        Selected camera information and status
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowGeneralInfoDetails(false)}
                      style={generalInfoBackButtonStyle}
                    >
                      ← Back
                    </button>
                  </div>

                  <div style={generalInfoContentGridStyle}>
                    <div style={generalInfoMainColumnStyle}>
                      <div style={generalInfoSectionLabelStyle}>
                        Camera information
                      </div>
                      <div style={generalInfoVerticalListStyle}>
                        {generalInfoRows.map(([label, value]) => (
                          <div
                            key={label}
                            style={generalInfoVerticalItemStyle}
                            title={String(value)}
                          >
                            <span style={generalInfoVerticalLabelStyle}>
                              {label}
                            </span>
                            <strong style={generalInfoVerticalValueStyle}>
                              {value}
                            </strong>
                          </div>
                        ))}
                      </div>

                      <div style={generalInfoStatusCardStyle}>
                        <div style={generalInfoSectionLabelStyle}>
                          Availability
                        </div>
                        <div style={generalInfoVerticalStatusListStyle}>
                          <AvailabilityLine
                            available={details.httpAvailable}
                            label="HTTP available"
                          />
                          <AvailabilityLine
                            available={details.p2pAvailable}
                            label="P&P ONVIF available"
                          />
                          <AvailabilityLine
                            available={details.onvifConnectionAvailable}
                            label="ONVIF connection available"
                          />
                          <AvailabilityLine
                            available={details.onvifAuthenticationSuccessful}
                            label="ONVIF authentication successful"
                          />
                          <AvailabilityLine
                            available={isOnline}
                            label={`Online from ${details.onlineFrom}`}
                          />
                        </div>
                      </div>

                      <div style={generalInfoStatusCardStyle}>
                        <div style={generalInfoSectionLabelStyle}>
                          ONVIF analytics modules
                        </div>
                        <div style={generalInfoVerticalStatusListStyle}>
                          <AnalyticsLine
                            label="Cell Motion Detector"
                            available={
                              details.analyticsModules.cellMotionDetector
                            }
                          />
                          <AnalyticsLine
                            label="Line Crossing Detector"
                            available={
                              details.analyticsModules.lineCrossingDetector
                            }
                          />
                          <AnalyticsLine
                            label="Intrusion Detector"
                            available={
                              details.analyticsModules.intrusionDetector
                            }
                          />
                          <AnalyticsLine
                            label="Tampering Detector"
                            available={
                              details.analyticsModules.tamperingDetector
                            }
                          />
                        </div>
                      </div>
                    </div>

                    <aside style={generalInfoThumbnailColumnStyle}>
                      <div style={generalInfoSectionLabelStyle}>
                        Latest thumbnail
                      </div>
                      <div style={generalInfoThumbnailCardStyle}>
                        {camera.thumbnailUrl ? (
                          <img
                            src={camera.thumbnailUrl}
                            alt={`${camera.name} latest thumbnail`}
                            style={generalInfoThumbnailStyle}
                          />
                        ) : (
                          <div style={generalInfoThumbnailEmptyStyle}>
                            No thumbnail available
                          </div>
                        )}
                        <div style={generalInfoThumbnailFooterStyle}>
                          <div style={{ minWidth: 0 }}>
                            <div style={generalInfoThumbnailNameStyle}>
                              {camera.name}
                            </div>
                            <div style={generalInfoThumbnailTimeStyle}>
                              Updated{" "}
                              {formatDate(
                                camera.thumbnailUpdatedAt || camera.updatedAt,
                              )}
                            </div>
                          </div>
                          <span
                            style={{
                              ...generalInfoLiveBadgeStyle,
                              ...(isOnline ? {} : generalInfoOfflineBadgeStyle),
                            }}
                          >
                            {cameraEnabled
                              ? isOnline
                                ? "Online"
                                : "Offline"
                              : "Disabled"}
                          </span>
                        </div>
                      </div>
                    </aside>
                  </div>
                </section>
              )}
            </div>
          )}

          {activeTab === "general" && (
            <section style={generalTabPanelStyle}>
              <div style={generalFormGridStyle}>
                <GeneralField label="Camera name">
                  <input
                    id={`camera-name-${camera.cameraId}`}
                    defaultValue={camera.name}
                    style={cameraGeneralInputStyle}
                  />
                </GeneralField>

                <GeneralField label="Manufacturer">
                  <input
                    value={details.manufacturer}
                    onChange={(event) =>
                      setDetails((current) => ({
                        ...current,
                        manufacturer: event.target.value,
                      }))
                    }
                    style={cameraGeneralInputStyle}
                  />
                </GeneralField>

                <GeneralField label="Model">
                  <input
                    value={details.model}
                    onChange={(event) =>
                      setDetails((current) => ({
                        ...current,
                        model: event.target.value,
                      }))
                    }
                    style={cameraGeneralInputStyle}
                  />
                </GeneralField>

                <GeneralField label="Serial number">
                  <input
                    value={details.serialNumber}
                    onChange={(event) =>
                      setDetails((current) => ({
                        ...current,
                        serialNumber: event.target.value,
                      }))
                    }
                    style={cameraGeneralInputStyle}
                  />
                </GeneralField>

                <div style={generalDescriptionFieldStyle}>
                  <GeneralField label="Description">
                    <textarea
                      value={details.description}
                      onChange={(event) =>
                        setDetails((current) => ({
                          ...current,
                          description: event.target.value,
                        }))
                      }
                      placeholder="Add a short camera description (optional)"
                      style={cameraDescriptionStyle}
                    />
                  </GeneralField>
                </div>
              </div>

              {pendingGatewayOnboarding && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 16,
                    padding: 16,
                    border: "1px solid #bfdbfe",
                    borderRadius: 12,
                    background: "#eff6ff",
                    marginBottom: 16,
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 800, color: "#1e3a8a", marginBottom: 4 }}>
                      Gateway setup pending
                    </div>
                    <div style={{ fontSize: 13, lineHeight: 1.5, color: "#475569" }}>
                      The camera is registered, but Qumra still needs to open its secure tunnel, authenticate through ONVIF, validate an H.264 profile, and assign an AWS Media Gateway.
                      {camera.onboardingError ? ` Last error: ${camera.onboardingError}` : ""}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => void finalizeGatewayCamera()}
                    disabled={finalizingGateway || savingGeneral}
                    style={{
                      ...btnPrimary,
                      flexShrink: 0,
                      ...(finalizingGateway || savingGeneral ? disabledButtonStyle : {}),
                    }}
                  >
                    {finalizingGateway ? "Completing..." : "Complete Gateway Setup"}
                  </button>
                </div>
              )}

              <div style={toggleListStyle}>
                <ToggleSetting
                  label="Enabled"
                  checked={enabledDraft}
                  onChange={changeEnabledDraft}
                  readOnly={savingGeneral || pendingGatewayOnboarding}
                  help={
                    pendingGatewayOnboarding
                      ? "This Gateway camera is registered but cannot be enabled until its secure tunnel and ONVIF profile are finalized."
                      : enabledDraft
                        ? "Live video and cloud recording run while the camera is enabled."
                        : "Live video and new recording are stopped. Previously recorded footage remains available."
                  }
                />
                <ToggleSetting
                  label="Motion events"
                  checked={details.motionEvents}
                  onChange={(checked) =>
                    setDetails((current) => ({
                      ...current,
                      motionEvents: checked,
                    }))
                  }
                  readOnly={savingGeneral}
                  help={motionEventsHelp(camera, details.motionEvents)}
                />
                <ToggleSetting
                  label="Camera Analytics"
                  checked={details.cameraAnalytics}
                  onChange={(checked) =>
                    setDetails((current) => ({
                      ...current,
                      cameraAnalytics: checked,
                    }))
                  }
                />
                <ToggleSetting
                  label="Mic"
                  checked={details.mic}
                  onChange={(checked) =>
                    setDetails((current) => ({
                      ...current,
                      mic: checked,
                    }))
                  }
                />
              </div>

              <section style={generalOverviewStyle}>
                <div style={sectionHeadingRowStyle}>
                  <div>
                    <div style={sectionEyebrowStyle}>Camera overview</div>
                    <div style={sectionTitleStyle}>Operational summary</div>
                  </div>
                  <span style={sectionStatusBadgeStyle}>Demo data</span>
                </div>

                <div style={overviewMetricGridStyle}>
                  <OverviewMetric label="Customer" value={camera.customerId || "Unassigned"} />
                  <OverviewMetric label="Public IP" value={camera.host || "Unavailable"} />
                  <OverviewMetric
                    label="ONVIF profile"
                    value={camera.onvif?.profileToken || "Not selected"}
                  />
                  <OverviewMetric
                    label="Last updated"
                    value={formatDate(camera.updatedAt)}
                  />
                </div>
              </section>
            </section>
          )}

          {activeTab === "archive" && (
            <ArchiveRetentionTab
              camera={camera}
              onUpdateCamera={onUpdateCamera}
            />
          )}

          {activeTab === "analytics" && (
            <AnalyticsWorkspace camera={camera} onUpdateCamera={onUpdateCamera} />
          )}

          {activeTab === "onvif" && (
            <OnvifConnectionTab
              camera={camera}
              onUpdateCamera={onUpdateCamera}
            />
          )}

          {activeTab === "quality" && (
            <VideoQualityCapabilitiesTab
              camera={camera}
              onUpdateCamera={onUpdateCamera}
              onDeviceInfo={(deviceInfo) => {
                setDetails((current) => ({
                  ...current,
                  ...deviceInfo,
                  onvifConnectionAvailable: true,
                  onvifAuthenticationSuccessful: true,
                }));
                onUpdateCamera({
                  ...camera,
                  deviceDetails: {
                    ...(camera.deviceDetails || {}),
                    ...deviceInfo,
                  },
                });
              }}
            />
          )}

          {activeTab === "billing" && (
            <BillingPlansTab
              camera={camera}
              onUpdateCamera={onUpdateCamera}
            />
          )}

          {activeTab === "location" && (
            <CameraLocationTab
              camera={camera}
              onUpdateCamera={onUpdateCamera}
            />
          )}

          {activeTab === "support" && (
            <section style={supportTabPanelStyle}>
              <div style={supportConnectionCardStyle}>
                <div>
                  <div style={supportCardEyebrowStyle}>Camera diagnostics</div>
                  <div style={supportCardTitleStyle}>
                    Connection and ONVIF device information
                  </div>
                  <div style={supportCardDescriptionStyle}>
                    Device identity is read live through ONVIF when the Quality
                    tab loads the selected camera.
                  </div>
                </div>

                <div style={supportStatusPillsStyle}>
                  <span style={supportSuccessPillStyle}>
                    <span style={supportSuccessDotStyle} />
                    ONVIF connection available
                  </span>
                  <span style={supportSuccessPillStyle}>
                    <span style={supportSuccessDotStyle} />
                    ONVIF authentication successful
                  </span>
                </div>
              </div>

              {camera.connectionType === "gateway" && (
                <div
                  style={{
                    ...supportDetailsCardStyle,
                    display: "grid",
                    gap: 16,
                    borderColor: "#bfdbfe",
                    background: "#f8fbff",
                  }}
                >
                  <div style={supportDetailsHeaderStyle}>
                    <div>
                      <div style={supportDetailsTitleStyle}>
                        Remote WebUI access
                      </div>
                      <div style={supportDetailsSubtitleStyle}>
                        Create a temporary 15-minute troubleshooting link. The
                        browser connects through Qumra's secure outbound Gateway
                        tunnel; no router port forwarding or public camera IP is
                        required.
                      </div>
                    </div>
                    <span style={supportDemoBadgeStyle}>15 minute session</span>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 10,
                      alignItems: "center",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => void createRemoteSupportSession("camera")}
                      disabled={creatingRemoteSupport || revokingRemoteSupport}
                      style={{
                        ...cameraSaveButtonStyle,
                        ...(creatingRemoteSupport || revokingRemoteSupport
                          ? disabledButtonStyle
                          : {}),
                      }}
                    >
                      {creatingRemoteSupport
                        ? "Creating..."
                        : "Generate Camera WebUI Access"}
                    </button>
                    <button
                      type="button"
                      onClick={() => void createRemoteSupportSession("gateway")}
                      disabled={
                        creatingRemoteSupport ||
                        revokingRemoteSupport ||
                        !camera.edgeGatewayId
                      }
                      style={{
                        ...cameraSecondaryActionButtonStyle,
                        ...(creatingRemoteSupport ||
                        revokingRemoteSupport ||
                        !camera.edgeGatewayId
                          ? disabledButtonStyle
                          : {}),
                      }}
                    >
                      Generate Gateway WebUI Access
                    </button>
                  </div>

                  {remoteSupportSession && (
                    <div
                      style={{
                        display: "grid",
                        gap: 10,
                        padding: 14,
                        border: "1px solid #bbf7d0",
                        borderRadius: 10,
                        background: "#f0fdf4",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          gap: 12,
                          justifyContent: "space-between",
                          alignItems: "center",
                          flexWrap: "wrap",
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: 800, color: "#166534" }}>
                            {remoteSupportSession.targetType === "camera"
                              ? "Camera WebUI link ready"
                              : "Gateway WebUI link ready"}
                          </div>
                          <div
                            style={{
                              marginTop: 3,
                              fontSize: 12,
                              color: "#4b5563",
                            }}
                          >
                            Expires {formatDate(remoteSupportSession.expiresAt)}
                          </div>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            gap: 8,
                            flexWrap: "wrap",
                          }}
                        >
                          <a
                            href={remoteSupportSession.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              ...cameraSaveButtonStyle,
                              display: "inline-flex",
                              alignItems: "center",
                              textDecoration: "none",
                            }}
                          >
                            Open WebUI ↗
                          </a>
                          <button
                            type="button"
                            onClick={() => void revokeRemoteSupportSession()}
                            disabled={revokingRemoteSupport}
                            style={{
                              ...cameraSecondaryActionButtonStyle,
                              ...(revokingRemoteSupport
                                ? disabledButtonStyle
                                : {}),
                            }}
                          >
                            {revokingRemoteSupport ? "Revoking..." : "Revoke"}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div style={supportDetailsCardStyle}>
                <div style={supportDetailsHeaderStyle}>
                  <div>
                    <div style={supportDetailsTitleStyle}>
                      Diagnostics details
                    </div>
                    <div style={supportDetailsSubtitleStyle}>
                      Credentials stay in AWS Secrets Manager. Device identity and
                      MAC addresses come from the camera's ONVIF device service.
                    </div>
                  </div>

                  <span style={supportDemoBadgeStyle}>Production data</span>
                </div>

                <div style={supportDetailsGridStyle}>
                  <SupportField label="Firmware version">
                    <input
                      value={details.firmwareVersion}
                      readOnly
                      style={supportReadOnlyInputStyle}
                    />
                  </SupportField>

                  <SupportField label="Hardware ID">
                    <input
                      value={details.hardwareId}
                      readOnly
                      style={supportReadOnlyInputStyle}
                    />
                  </SupportField>

                  <SupportField label="MAC address">
                    <input
                      value={details.macAddress}
                      readOnly
                      style={supportReadOnlyInputStyle}
                    />
                  </SupportField>

                  <SupportField label="ONVIF credentials">
                    <input
                      value={camera.username ? "Configured securely" : "Not configured"}
                      readOnly
                      style={supportReadOnlyInputStyle}
                    />
                  </SupportField>

                  <SupportField label="Selected ONVIF profile">
                    <input
                      value={camera.onvif?.profileToken || "Not selected"}
                      readOnly
                      style={supportReadOnlyInputStyle}
                    />
                  </SupportField>

                  <SupportField label="Public IP">
                    <input
                      value={details.publicIp}
                      readOnly
                      style={supportReadOnlyInputStyle}
                    />
                  </SupportField>

                  <SupportField label="Customer ID">
                    <input
                      value={camera.customerId}
                      readOnly
                      style={supportReadOnlyInputStyle}
                    />
                  </SupportField>
                </div>

                {supportStatus && (
                  <div style={supportMessageStyle}>{supportStatus}</div>
                )}
              </div>
            </section>
          )}
        </div>

        <footer style={cameraDetailsFooterStyle}>
          {activeTab === "general" && (
            <button
              type="button"
              onClick={() => void saveGeneral()}
              disabled={savingGeneral}
              style={{
                ...cameraSaveButtonStyle,
                ...(savingGeneral ? disabledButtonStyle : {}),
              }}
            >
              {savingGeneral ? "Saving..." : "Save"}
            </button>
          )}


          {generalStatus && activeTab === "general" && (
            <span
              role={generalStatusIsError ? "alert" : "status"}
              style={{
                ...cameraFooterStatusStyle,
                ...(generalStatusIsError ? { color: "#b91c1c" } : {}),
              }}
            >
              {generalStatus}
            </span>
          )}

          {deleteStatus && (
            <span
              role="alert"
              style={{ ...cameraFooterStatusStyle, color: "#b91c1c" }}
            >
              {deleteStatus}
            </span>
          )}

          {canDelete && (
            <button
              type="button"
              onClick={() => void requestDeleteCamera()}
              disabled={deletingCamera || savingGeneral}
              style={{
                ...cameraSecondaryActionButtonStyle,
                background: "#b91c1c",
                borderColor: "#991b1b",
                color: "#ffffff",
                ...(deletingCamera || savingGeneral ? disabledButtonStyle : {}),
              }}
            >
              {deletingCamera
                ? "Deleting..."
                : String(camera.deletionStatus || "").toUpperCase() === "FAILED"
                  ? "Retry permanent delete"
                  : "Delete camera"}
            </button>
          )}

          <button
            type="button"
            onClick={onClose}
            disabled={savingGeneral}
            style={{
              ...cameraCloseButtonStyle,
              ...(savingGeneral ? disabledButtonStyle : {}),
            }}
          >
            Close
          </button>
        </footer>
      </section>
    </div>
  );
}

function CameraTabButton({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        ...cameraTabStyle,
        ...(active ? cameraTabActiveStyle : {}),
      }}
    >
      {label}
    </button>
  );
}

function StatusTag({
  label,
  tone,
}: {
  label: string;
  tone: "green" | "blue" | "red";
}) {
  const colors =
    tone === "green"
      ? {
          background: "#5cbf63",
          border: "#4aa452",
        }
      : tone === "red"
        ? {
            background: "#dc5b5b",
            border: "#bf4444",
          }
        : {
            background: "#4d88c7",
            border: "#3770ad",
          };

  return (
    <span
      style={{
        ...cameraStatusTagStyle,
        ...colors,
      }}
    >
      {label}
    </span>
  );
}

function AvailabilityLine({
  available,
  label,
}: {
  available: boolean;
  label: string;
}) {
  return (
    <div style={availabilityLineStyle}>
      <span
        style={{
          color: available ? "#49a84f" : "#c94b4b",
          marginRight: 8,
        }}
      >
        •
      </span>
      <span
        style={{
          color: available ? "#49a84f" : "#c94b4b",
        }}
      >
        {label}
      </span>
    </div>
  );
}

function AnalyticsLine({
  label,
  available,
}: {
  label: string;
  available: boolean;
}) {
  return (
    <div style={analyticsLineStyle}>
      <span>•</span>
      <span>{label} — </span>
      <strong
        style={{
          color: available ? "#49a84f" : "#c94b4b",
        }}
      >
        {available ? "Available" : "Unavailable"}
      </strong>
    </div>
  );
}

function GeneralField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label style={generalFieldStyle}>
      <span>{label}</span>
      {children}
    </label>
  );
}

function ToggleSetting({
  label,
  checked,
  onChange,
  readOnly = false,
  help,
}: {
  label: string;
  checked: boolean;
  onChange?: (checked: boolean) => void;
  readOnly?: boolean;
  help?: string;
}) {
  return (
    <div style={toggleSettingRowStyle}>
      <div>
        <div style={toggleSettingLabelStyle}>{label}</div>
        {help && <div style={toggleSettingHelpStyle}>{help}</div>}
      </div>

      <button
        type="button"
        aria-pressed={checked}
        aria-label={`${label}: ${checked ? "on" : "off"}`}
        disabled={readOnly}
        onClick={() => onChange?.(!checked)}
        style={{
          ...toggleTrackStyle,
          background: checked ? "#5cc568" : "#d9d9d9",
          opacity: readOnly ? 0.75 : 1,
          cursor: readOnly ? "default" : "pointer",
        }}
      >
        <span
          style={{
            ...toggleThumbStyle,
            transform: checked ? "translateX(16px)" : "translateX(0)",
          }}
        />
      </button>
    </div>
  );
}

function SupportField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label style={supportFieldStyle}>
      <span style={supportFieldLabelStyle}>{label}</span>
      {children}
    </label>
  );
}

function QualityRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div style={qualityRowStyle}>
      <div style={qualityRowLabelStyle}>{label}</div>
      <div style={qualityRowContentStyle}>{children}</div>
    </div>
  );
}

function OverviewMetric({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div style={overviewMetricStyle}>
      <span style={overviewMetricLabelStyle}>{label}</span>
      <strong style={overviewMetricValueStyle}>{value}</strong>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={fieldLabelStyle}>{label}</div>
      {children}
    </div>
  );
}
function isValidPort(port: number) {
  return Number.isInteger(port) && port >= 1 && port <= 65535;
}
function formatDate(value?: string) {
  if (!value) {
    return "Unknown";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "Unknown";
  }
  return date.toLocaleString();
}
